import { useCallback, useEffect, useState } from "react"
import type { AdminClient, OAuthConnectionSummary } from "../api/admin-client.ts"

/**
 * Claude 연결 관리 섹션 — OAuth 2.1 (DCR + PKCE) 로 발급된 연결 목록 + 회수 버튼.
 *
 * - 서버 라우트(`/admin/services/:slug/oauth/connections`) 미구현 시 404 → 빈 목록 안내 표시.
 * - 실제 라우트 준비 후 자동으로 목록이 활성화된다.
 * - 연결 해제: 확인 dialog → `revokeOAuthConnection` 호출 → 목록 리프레시.
 */
export function OAuthConnectionsSection(props: { readonly client: AdminClient; readonly slug: string }) {
	const [connections, setConnections] = useState<ReadonlyArray<OAuthConnectionSummary>>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | undefined>()
	const [revokingId, setRevokingId] = useState<string | undefined>()

	const refresh = useCallback(async () => {
		try {
			setError(undefined)
			const list = await props.client.listOAuthConnections(props.slug)
			setConnections(list)
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setLoading(false)
		}
	}, [props.client, props.slug])

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		props.client
			.listOAuthConnections(props.slug)
			.then((list) => {
				if (!cancelled) {
					setConnections(list)
					setLoading(false)
				}
			})
			.catch((e: Error) => {
				if (!cancelled) {
					setError(e.message)
					setLoading(false)
				}
			})
		return () => {
			cancelled = true
		}
	}, [props.client, props.slug])

	async function handleRevoke(conn: OAuthConnectionSummary) {
		const confirmed = window.confirm(
			`"${conn.clientName}" 의 연결을 해제하시겠습니까?\n\n해제 즉시 해당 Claude 클라이언트의 접근이 차단됩니다.`
		)
		if (!confirmed) return
		setRevokingId(conn.clientId)
		try {
			await props.client.revokeOAuthConnection(props.slug, conn.clientId)
			await refresh()
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setRevokingId(undefined)
		}
	}

	return (
		<div className="panel">
			<div className="panel-head">
				<h3>Claude 연결 관리</h3>
			</div>

			{error && (
				<div className="alert alert-danger" style={{ marginBottom: 14 }}>
					{error}
				</div>
			)}

			{loading ? (
				<p className="muted" style={{ fontSize: 13 }}>
					불러오는 중...
				</p>
			) : connections.length === 0 ? (
				<EmptyConnectionsState />
			) : (
				<ConnectionsTable connections={connections} revokingId={revokingId} onRevoke={handleRevoke} />
			)}
		</div>
	)
}

/**
 * 빈 목록 안내 — 라우트 미구현 또는 아직 연결된 Claude 없을 때.
 */
function EmptyConnectionsState() {
	return (
		<div
			style={{
				padding: "var(--pk-space-8) var(--pk-space-4)",
				textAlign: "center",
				color: "var(--pk-text-muted)",
				fontSize: 13,
				lineHeight: 1.6
			}}
		>
			<div
				style={{
					width: 48,
					height: 48,
					margin: "0 auto var(--pk-space-4)",
					borderRadius: "var(--pk-radius-lg)",
					background: "var(--pk-accent-subtle)",
					display: "grid",
					placeItems: "center",
					fontSize: 24
				}}
				aria-hidden
			>
				🤖
			</div>
			<p style={{ margin: "0 0 var(--pk-space-2)", fontWeight: 600, color: "var(--pk-text-secondary)" }}>
				OAuth 로 연결된 Claude 가 아직 없습니다.
			</p>
			<p style={{ margin: 0 }}>
				MCP URL 을 claude.ai 의 Settings → Connectors 에 입력하면 자동으로 나타납니다.
			</p>
		</div>
	)
}

/**
 * 연결 목록 테이블.
 */
function ConnectionsTable(props: {
	readonly connections: ReadonlyArray<OAuthConnectionSummary>
	readonly revokingId: string | undefined
	readonly onRevoke: (conn: OAuthConnectionSummary) => void
}) {
	return (
		<div style={{ overflow: "hidden", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}>
			<table className="table">
				<thead>
					<tr>
						<th>클라이언트</th>
						<th>MCP 서버</th>
						<th>스코프</th>
						<th>마지막 사용</th>
						<th style={{ width: 100 }} />
					</tr>
				</thead>
				<tbody>
					{props.connections.map((conn) => (
						<tr key={conn.clientId}>
							<td>
								<div style={{ fontWeight: 500 }}>{conn.clientName}</div>
								<code className="inline" style={{ fontSize: 10 }}>
									{conn.clientId}
								</code>
							</td>
							<td>
								<code className="inline" style={{ fontSize: 11 }}>
									{conn.mcpServerId}
								</code>
							</td>
							<td>
								<div className="chips">
									{conn.scopes.map((s) => (
										<span key={s} className="chip">
											{s}
										</span>
									))}
								</div>
							</td>
							<td className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
								{conn.lastUsedAt ? new Date(conn.lastUsedAt).toLocaleDateString("ko-KR") : "—"}
							</td>
							<td>
								<button
									type="button"
									className="btn btn-danger btn-sm"
									disabled={props.revokingId === conn.clientId}
									onClick={() => props.onRevoke(conn)}
								>
									{props.revokingId === conn.clientId ? "해제 중..." : "연결 해제"}
								</button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}
